import React, {useState, useMemo, memo} from 'react';
import {Props} from './ac-tooltip.proptypes';
import {usePopperTooltip} from 'react-popper-tooltip';
import clsx from 'clsx';

// Imports => Utilities
import {AcIsSet, AcIsString, AcIsFunction} from '@asdf-utils';

const _CLASSES = {
  MAIN: 'ac-tooltip',
  TRIGGER: 'ac-tooltip__trigger',
  ARROW: 'ac-tooltip__arrow',
  WRAPPER: {
    MAIN: 'ac-tooltip-wrapper',
    NOPADDING: 'ac-tooltip-wrapper--no-padding',
    VISIBLE: 'ac-tooltip-wrapper--visible',
    TRIGGER: {
      HOVER: 'ac-tooltip-wrapper--trigger-hover',
      CLICK: 'ac-tooltip-wrapper--trigger-click',
    },
    THEME: {
      LIGHT: 'ac-tooltip-wrapper--theme-light',
      DARK: 'ac-tooltip-wrapper--theme-dark',
    },
  },
};

const AcTooltip = memo(
  ({
    className,
    dark,
    noPadding,
    children,
    content,
    trigger = 'hover',
    placement = 'left',
    createRefWrapper = true,
    closeOnOutsideClick,
    closeOnTriggerHidden,
    defaultVisible,
    delayHide,
    delayShow,
    followCursor = true,
    interactive,
    mutationObserverOptions,
    offset,
    onVisibleChange,
  }) => {
    const [visibility, setVisibility] = useState(false);

    const {getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible} =
      usePopperTooltip({
        closeOnOutsideClick,
        closeOnTriggerHidden,
        defaultVisible,
        delayHide,
        delayShow,
        followCursor,
        interactive,
        mutationObserverOptions,
        offset: offset ? offset : placement === 'bottom' && followCursor ? [0, 32] : [0, 16],
        onVisibleChange,
        placement,
        trigger,
      });

    const getArrowClassNames = useMemo(() => {
      return clsx(_CLASSES.ARROW);
    }, []);

    const getTriggerClassNames = useMemo(() => {
      return clsx(_CLASSES.TRIGGER);
    }, []);

    const getWrapperClassNames = useMemo(() => {
      return clsx(
        _CLASSES.WRAPPER.MAIN,
        noPadding && _CLASSES.WRAPPER.NOPADDING,
        visibility && _CLASSES.WRAPPER.VISIBLE,
        trigger && _CLASSES.WRAPPER.TRIGGER[trigger.toUpperCase()],
        dark && _CLASSES.WRAPPER.THEME.DARK,
        !dark && _CLASSES.WRAPPER.THEME.LIGHT
      );
    }, [noPadding, visibility, trigger, dark]);

    const getMainClassNames = useMemo(() => {
      return clsx(_CLASSES.MAIN, className);
    }, [className]);

    const renderTriggerElement = useMemo(() => {
      if (!AcIsSet(children)) return null;

      if (!createRefWrapper && AcIsString(children)) {
        return (
          <span ref={setTriggerRef} className={getTriggerClassNames}>
            {children}
          </span>
        );
      }

      return React.cloneElement(children, {
        ref: setTriggerRef,
        className: getTriggerClassNames,
      });
    }, [children]);

    const renderTooltipElement = useMemo(() => {
      if (!visible) return null;

      return (
        <div>
          {AcIsFunction(content) ? content({setVisibility, visibilityChange}) : content}
          <div {...getArrowProps({className: getArrowClassNames})} />
        </div>
      );
    }, [visible, setTooltipRef, content]);

    const visibilityChange = event => {
      if (event && event.persist) event.persist();

      setVisibility(event);
      if (onVisibilityChange && AcIsFunction(onVisibleChange)) onVisibilityChange(event);
    };

    return (
      <div className={getMainClassNames}>
        {renderTriggerElement}
        <div
          ref={setTooltipRef}
          {...getTooltipProps({className: visible ? getWrapperClassNames : ''})}
        >
          {renderTooltipElement}
        </div>
      </div>
    );
  }
);

AcTooltip.displayName = 'AcTooltip';
AcTooltip.propTypes = Props;

export {AcTooltip};
export default AcTooltip;
